import React, { useState, useEffect } from 'react';
import api from '../services/api';
import API_BASE from '../config';
import ReactionCard from './ReactionCard';
import './ReactionList.css';

const randomSeed = () => (Math.random() * 2 - 1).toFixed(8);

const ReactionList = ({ linkPrefix = '/reaction/' }) => {
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReactions, setTotalReactions] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [reactionTypes, setReactionTypes] = useState([]);
  const [seed, setSeed] = useState(randomSeed);
  const perPage = 20;

  useEffect(() => {
    fetch(`${API_BASE}/reaction-types`)
      .then(r => r.json())
      .then(data => setReactionTypes(data.filter(t => t.reaction_type)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadReactions();
  }, [currentPage, typeFilter, seed]);

  const loadReactions = async () => {
    try {
      setLoading(true);
      const data = await api.getReactions(currentPage, perPage, typeFilter, seed);
      setReactions(data.reactions);
      setTotalPages(data.pages);
      setTotalReactions(data.total);
      setError(null);
    } catch (err) {
      setError('Failed to load reactions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeFilter = (value) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };

  const handleReshuffle = () => {
    setSeed(randomSeed());
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  if (loading) {
    return <div className="loading">Loading reactions...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="reaction-list-container">
      <div className="reaction-list-header">
        <p className="subtitle">
          Showing {reactions.length} of {totalReactions} reactions
          (Page {currentPage} of {totalPages})
        </p>
        <div className="browse-filter-row">
          <label className="browse-filter-label">Filter by type:</label>
          <select
            className="browse-type-select"
            value={typeFilter}
            onChange={e => handleTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            {reactionTypes.map(t => (
              <option key={t.reaction_type} value={t.reaction_type}>
                {t.reaction_type} ({t.count})
              </option>
            ))}
          </select>
          {typeFilter && (
            <button className="browse-clear-btn" onClick={() => handleTypeFilter('')}>
              Clear
            </button>
          )}
          <button className="browse-shuffle-btn" onClick={handleReshuffle} title="Show a new random selection">
            ⟳ Shuffle
          </button>
        </div>
      </div>

      <div className="reactions-grid">
        {reactions.map((reaction) => (
          <ReactionCard key={reaction.id} reaction={reaction} linkPrefix={linkPrefix} />
        ))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="pagination">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="pagination-button"
      >
        ← Previous
      </button>

      <div className="pagination-numbers">
        {getPageNumbers().map((page, idx) => (
          page === '...' ? (
            <span key={idx} className="pagination-ellipsis">...</span>
          ) : (
            <button
              key={idx}
              onClick={() => onPageChange(page)}
              className={`pagination-number ${currentPage === page ? 'active' : ''}`}
            >
              {page}
            </button>
          )
        ))}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="pagination-button"
      >
        Next →
      </button>
    </div>
  );
};

export default ReactionList;

